// ─────────────────────────────────────────────────────────────────────────────
// 🔑 MOVING A BORROWER BETWEEN PARTNERS (Jesse, 2026-10-27 — backend utils/partnerAttribution).
// The only way a borrower moves: allow-listed staff, a reason and a sentence, locked once a commission
// is recorded. The commission outcome follows the reason — attribution error pays the correct partner,
// a non-performance takeover pays no one, any other reason needs new / original / split / none, and
// nothing pays at closing until it is recorded. The attribution log below is append-only.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const OUTCOME_LABELS = { payable_new: 'Pay the new partner', payable_original: 'Pay the original partner', split: 'Split', none: 'No commission' };
const FIXED = { attribution_error: 'The correct (new) partner is paid.', nonperformance_takeover: 'No one is paid (§4.3).' };
const box = { height: 34, padding: '0 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 };

export default function PartnerReassignPanel({ userId, token, partnersList, currentPartnerId, onSaved, onCancel }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [log, setLog] = useState(null);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState('');
  const [splitOrig, setSplitOrig] = useState('50');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${userId}/partner/attribution`, { headers }); setLog(r.data); }
    catch (_) { setLog({ events: [], plan: null, reasons: {}, dispositions: [] }); }
  }, [userId, token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const nameOf = (id) => { const p = (partnersList || []).find((x) => Number(x.id) === Number(id)); return p ? `${p.first_name} ${p.last_name}` : (id ? `Partner #${id}` : 'none'); };
  const toId = to ? Number(to) : null;
  const splitBody = () => [{ partnerId: Number(currentPartnerId), pct: Number(splitOrig) }, { partnerId: toId, pct: 100 - Number(splitOrig) }];

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = { referral_partner_id: toId, reason_code: reason, reason_text: text };
      if (!FIXED[reason] && outcome) { body.commission_disposition = outcome; if (outcome === 'split') body.split = splitBody(); }
      const r = await axios.patch(`${API}/api/admin/borrowers/${userId}/partner`, body, { headers });
      await load();
      onSaved && onSaved(r.data.user);
    } catch (e) { setError(e?.response?.data?.error || 'Could not save.'); }
    setSaving(false);
  };
  const recordOutcome = async (value) => {
    setSaving(true); setError(null);
    try { await axios.post(`${API}/api/admin/borrowers/${userId}/partner/commission-outcome`, { commission_disposition: value }, { headers }); await load(); }
    catch (e) { setError(e?.response?.data?.error || 'Could not record the outcome.'); }
    setSaving(false);
  };

  const reasons = (log && log.reasons) || {};
  const held = log && log.plan && log.plan.held === 'disposition_required';
  return (
    <div data-testid="admin-partner-reassign" style={{ marginTop: 8, display: 'grid', gap: 8, padding: 12, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', maxWidth: 640 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select data-testid="admin-partner-reassign-to" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...box, minWidth: 220 }}>
          <option value="">— No referral partner —</option>
          {(partnersList || []).map((p) => (
            <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.partner_type === 'loan_officer' ? 'LO' : 'Realtor'}){p.brokerage ? ` · ${p.brokerage}` : ''}</option>
          ))}
        </select>
        <select data-testid="admin-partner-reassign-reason" value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...box, minWidth: 200 }}>
          <option value="">Reason…</option>
          {Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <input data-testid="admin-partner-reassign-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Why, in a sentence (required)" style={{ ...box, width: '100%' }} />
      {reason && FIXED[reason] && <div style={{ fontSize: 12, color: '#374151' }}>Commission: {FIXED[reason]}</div>}
      {reason && !FIXED[reason] && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: '#374151' }}>
          <span style={{ fontWeight: 600 }}>Commission:</span>
          <select data-testid="admin-partner-reassign-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} style={box}>
            <option value="">Record later (nothing pays until recorded)</option>
            {Object.entries(OUTCOME_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {outcome === 'split' && (
            <span>Original <input type="number" min="1" max="99" value={splitOrig} onChange={(e) => setSplitOrig(e.target.value)} style={{ ...box, width: 64 }} />% · New {100 - Number(splitOrig || 0)}%</span>
          )}
        </div>
      )}
      {error && <div role="alert" style={{ fontSize: 12, color: '#7f1d1d', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, padding: '6px 10px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button data-testid="admin-partner-reassign-save" disabled={saving || !reason || !text.trim()} onClick={save}
          style={{ padding: '6px 14px', borderRadius: 8, background: (!reason || !text.trim()) ? '#e5e7eb' : '#3a6ad9', color: (!reason || !text.trim()) ? '#6b7280' : '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Move borrower'}
        </button>
        <button onClick={onCancel} style={{ padding: '6px 10px', borderRadius: 8, background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
      </div>
      {held && (
        <div data-testid="admin-partner-outcome-needed" style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          Commission outcome not recorded — nothing pays until it is.
          {['payable_new', 'payable_original', 'none'].map((k) => (
            <button key={k} disabled={saving} onClick={() => recordOutcome(k)} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, cursor: 'pointer' }}>{OUTCOME_LABELS[k]}</button>
          ))}
        </div>
      )}
      {log && log.events && log.events.length > 0 && (
        <div data-testid="admin-partner-attribution-log" style={{ fontSize: 12, color: '#374151', borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Attribution log</div>
          {log.events.map((e) => (
            <div key={e.id} style={{ lineHeight: 1.6 }}>
              {new Date(e.created_at).toLocaleString()} · {e.event}
              {e.event === 'linked' && ` → ${nameOf(e.to_partner_id)} (${e.source})${e.commission_eligible === false ? ' · not commission-eligible (§4.3)' : ''}`}
              {e.event === 'contested' && ` · ${nameOf(e.to_partner_id)} claimed (${e.source}); stays with ${nameOf(e.from_partner_id)}`}
              {(e.event === 'reassigned' || e.event === 'cleared') && ` · ${nameOf(e.from_partner_id)} → ${nameOf(e.to_partner_id)} · ${reasons[e.reason_code] || e.reason_code}: ${e.reason_text || ''}`}
              {e.commission_disposition && ` · commission: ${e.commission_disposition.replace(/_/g, ' ')}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
